// Asking for a rating at a happy moment, and at no other.
//
// The system sheets do the asking (SKStoreReviewController on iOS, the Play
// In-App Review API on Android, through the `Review` plugin); this file decides
// WHEN, and the decision is pure so it can be tested to the minute.
//
// THE HAPPY MOMENT. A followed trip has finished, nothing went wrong on it:
// nobody is still following anything, no leg was cancelled or disrupted, and
// the train got there within two minutes of its schedule. A followed way-round
// plan (a trip with changes) is a followed trip, so it is covered by the same
// rule when it arrives well, which is the only time it is worth asking.
//
// THE LIMITS, which are the part that keeps this from being nagging:
//   never in the first 3 days after install;
//   at most once per 120 days, counted from the last time we asked (whether or
//   not the system chose to show anything: it has its own quota, and not
//   knowing is why we count asks and not shows);
//   never while another follow is running;
//   never with the app out of sight.
//
// STORAGE: two dates in the app's own preferences. Nothing leaves the phone.

export const KEY = 'reviewPrompt';
export const MIN_INSTALL_DAYS = 3;
export const MIN_GAP_DAYS = 120;
/** "Within 2 minutes of schedule". */
export const ON_TIME_MINUTES = 2;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Was this finished trip a good one? Pure. `follow` is the held follow object
 * (see src/follow.js): `live.legs[]` with `delayMinutes`, `cancelled`,
 * `disrupted`.
 */
export function happyArrival(follow) {
  const legs = follow?.live?.legs;
  if (!Array.isArray(legs) || !legs.length) return false;
  if (legs.some((l) => l?.cancelled || l?.disrupted)) return false;
  const delay = Number(legs[legs.length - 1]?.delayMinutes);
  return Number.isFinite(delay) && delay <= ON_TIME_MINUTES;
}

/**
 * May we ask now? Pure. Returns `{ ok: true }` or `{ ok: false, why }`.
 *
 * @param {object} s
 * @param {number} s.now            epoch ms
 * @param {number|null} s.firstSeenAt  when this install first ran this code
 * @param {number|null} s.askedAt      when we last asked
 * @param {number} s.activeFollows  follows still running
 * @param {boolean} s.visible       the app is in the foreground
 */
export function mayAsk({ now, firstSeenAt = null, askedAt = null, activeFollows = 0, visible = true } = {}) {
  if (!visible) return { ok: false, why: 'hidden' };
  if (activeFollows > 0) return { ok: false, why: 'mid-follow' };
  if (!Number.isFinite(firstSeenAt)) return { ok: false, why: 'new-install' };
  if (now - firstSeenAt < MIN_INSTALL_DAYS * DAY_MS) return { ok: false, why: 'new-install' };
  if (Number.isFinite(askedAt) && now - askedAt < MIN_GAP_DAYS * DAY_MS) return { ok: false, why: 'asked-lately' };
  return { ok: true };
}

export function createReviewPrompt({
  getPlugin = () => null,
  store = {},
  now = () => Date.now(),
  isVisible = () => (typeof document === 'undefined' ? true : document.visibilityState !== 'hidden'),
} = {}) {
  const read = () => {
    const raw = store[KEY];
    return raw && typeof raw === 'object' ? raw : {};
  };
  const write = (patch) => { store[KEY] = { ...read(), ...patch }; };

  /** Called once at launch: the install clock starts the first time this runs. */
  function noteLaunch() {
    try { if (!Number.isFinite(read().firstSeenAt)) write({ firstSeenAt: now() }); } catch { /* storage unavailable */ }
  }

  /**
   * A followed trip ended. Resolves true when the sheet was requested. NEVER
   * THROWS and never waits on the system's answer for anything that matters.
   *
   * @param {object} follow         the follow that just ended
   * @param {string} reason         why it ended ('arrived' is the only happy one)
   * @param {number} activeFollows  follows still running AFTER this one went
   */
  async function afterFollow(follow, reason, activeFollows = 0) {
    try {
      if (reason !== 'arrived' || !happyArrival(follow)) return false;
      const plugin = getPlugin();
      if (!plugin) return false;
      const s = read();
      const verdict = mayAsk({
        now: now(), firstSeenAt: s.firstSeenAt, askedAt: s.askedAt, activeFollows, visible: isVisible(),
      });
      if (!verdict.ok) return false;
      // Recorded BEFORE asking: a crash or a hang in the system sheet must not
      // turn into a second ask next time.
      write({ askedAt: now() });
      await plugin.request();
      return true;
    } catch {
      return false;
    }
  }

  return { noteLaunch, afterFollow };
}

// ── The app's instance ───────────────────────────────────────────────────────

import { isNative, plugin as nativePlugin } from './core/native.js';
import { prefs } from './core/storage.js';
import { isDemo } from './core/demo.js';

export const reviewPrompt = createReviewPrompt({
  // Bound, never the proxy: awaiting a Capacitor plugin proxy hangs.
  getPlugin: () => {
    if (!isNative || isDemo.value) return null;
    const p = nativePlugin('Review');
    return p ? { request: () => p.request() } : null;
  },
  store: prefs,
});
